import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { VoiceService } from "@/services/integrations/elevenlabs/VoiceService";

const voiceService = VoiceService.getInstance();

// Query keys
export const voiceKeys = {
  all: ["voices"] as const,
  lists: () => [...voiceKeys.all, "list"] as const,
  list: (type: string) => [...voiceKeys.lists(), type] as const,
  search: (query: string) => [...voiceKeys.all, "search", query] as const,
};

/**
 * Hook to fetch recommended voices with pagination support
 */
export const useRecommendedVoices = (options?: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
  category?: string;
  accent?: string;
  language?: string;
  age?: string;
  gender?: string;
} | null) => {
  const { enabled = options !== null, page = 0, pageSize = 100, search, category, accent, language, age, gender } = options || {};

  // Build query key with filters to ensure cache separation
  const queryKeyString = [
    `recommended-page-${page}-size-${pageSize}`,
    search ? `search-${search}` : "",
    category && category !== "all" ? `category-${category}` : "",
    accent && accent !== "all" ? `accent-${accent}` : "",
    language && language !== "all" ? `language-${language}` : "",
    age && age !== "all" ? `age-${age}` : "",
    gender && gender !== "all" ? `gender-${gender}` : "",
  ]
    .filter(Boolean)
    .join("_");

  return useQuery({
    queryKey: voiceKeys.list(queryKeyString),
    queryFn: () =>
      voiceService.getRecommendedVoices({
        page,
        pageSize,
        search,
        category,
        accent,
        language,
        age,
        gender,
      }),
    staleTime: 3 * 60 * 60 * 1000,
    gcTime: 6 * 60 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
    enabled,
  });
};

/**
 * Hook to fetch all available voices with pagination and filter support
 */
export const useVoices = (options?: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  category?: string;
  accent?: string;
  language?: string;
  age?: string;
  gender?: string;
}) => {
  const { enabled = true, page = 0, pageSize = 100, category, accent, language, age, gender } = options || {};

  // Build query key with filters to ensure cache separation
  const queryKeyString = [
    `all-page-${page}-size-${pageSize}`,
    category && category !== "all" ? `category-${category}` : "",
    accent && accent !== "all" ? `accent-${accent}` : "",
    language && language !== "all" ? `language-${language}` : "",
    age && age !== "all" ? `age-${age}` : "",
    gender && gender !== "all" ? `gender-${gender}` : "",
  ]
    .filter(Boolean)
    .join("_");

  return useQuery({
    queryKey: voiceKeys.list(queryKeyString),
    queryFn: () =>
      voiceService.getAllVoices({
        page,
        pageSize,
        category,
        accent,
        language,
        age,
        gender,
      }),
    staleTime: 3 * 60 * 60 * 1000,
    gcTime: 6 * 60 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
    enabled,
  });
};

export const useVoice = (voiceId: string | undefined) => {
  return useQuery({
    queryKey: [...voiceKeys.all, "detail", voiceId],
    queryFn: () => (voiceId ? voiceService.getVoiceById(voiceId) : Promise.resolve(null)),
    staleTime: 3 * 60 * 60 * 1000,
    gcTime: 6 * 60 * 60 * 1000,
    enabled: !!voiceId,
    retry: false,
  });
};

export const useRefreshVoices = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await queryClient.invalidateQueries({ queryKey: voiceKeys.lists() });
    },
    onSuccess: () => {
      toast.success("Voice data has been refreshed");
    },
    onError: (error) => {
      console.error("Failed to refresh voices:", error);
      toast.error("Failed to refresh voice data.");
    },
  });
};

export const useSharedVoices = (options?: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
  category?: string;
  accent?: string;
  language?: string;
  age?: string;
  gender?: string;
}) => {
  const { enabled = true, page = 0, pageSize = 100, search, category, accent, language, age, gender } = options || {};

  // Build query key with filters to ensure cache separation
  const queryKeyString = [
    `shared-page-${page}-size-${pageSize}`,
    search ? `search-${search}` : "",
    category && category !== "all" ? `category-${category}` : "",
    accent && accent !== "all" ? `accent-${accent}` : "",
    language && language !== "all" ? `language-${language}` : "",
    age && age !== "all" ? `age-${age}` : "",
    gender && gender !== "all" ? `gender-${gender}` : "",
  ]
    .filter(Boolean)
    .join("_");

  return useQuery({
    queryKey: voiceKeys.list(queryKeyString),
    queryFn: () =>
      voiceService.getSharedVoices({
        page,
        pageSize,
        search,
        category,
        accent,
        language,
        age,
        gender,
      }),
    staleTime: 3 * 60 * 60 * 1000,
    gcTime: 6 * 60 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
    enabled,
  });
};

export const useVoiceCategories = (enabled: boolean = true) => {
  const { data: voicesResult } = useVoices({ enabled });
  const voices = voicesResult?.voices;

  const categories =
    voices?.reduce((acc, voice) => {
      if (voice.category && !acc.includes(voice.category)) {
        acc.push(voice.category);
      }
      return acc;
    }, [] as string[]) || [];

  return categories.sort();
};
